import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Card, Spin, Alert, Table } from 'antd';
import MainLayout from '../../layouts/MainLayout';
import { financialApi } from '../../utils/financialApi';
import { extractApiUserMessage } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

const { Title, Text } = Typography;

const AgingReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await financialApi.getAgingReport();
        const agingReport = res?.data?.aging_report || {};
        setData(agingReport);
      } catch (err) {
        setError(extractApiUserMessage(err) || 'خطا در دریافت گزارش سنی بدهی');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-[60vh]">
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  const chartData = data ? [
    { 
      name: data?.not_due?.label || 'سررسید نشده', 
      value: data?.not_due?.total ?? data?.not_due_yet ?? 0, 
      count: data?.not_due?.count ?? 0,
      color: '#10B981' 
    },
    { 
      name: data?.['1_to_7_days']?.label || '۱ تا ۷ روز', 
      value: data?.['1_to_7_days']?.total ?? 0, 
      count: data?.['1_to_7_days']?.count ?? 0,
      color: '#F59E0B' 
    },
    { 
      name: data?.['8_to_30_days']?.label || '۸ تا ۳۰ روز', 
      value: data?.['8_to_30_days']?.total ?? data?.['1_to_30_days'] ?? 0, 
      count: data?.['8_to_30_days']?.count ?? 0,
      color: '#F97316' 
    },
    { 
      name: data?.['31_to_60_days']?.label || '۳۱ تا ۶۰ روز', 
      value: data?.['31_to_60_days']?.total ?? 0, 
      count: data?.['31_to_60_days']?.count ?? 0,
      color: '#EF4444' 
    },
    { 
      name: data?.over_60_days?.label || 'بیش از ۶۰ روز', 
      value: data?.over_60_days?.total ?? data?.over_90_days ?? 0, 
      count: data?.over_60_days?.count ?? 0,
      color: '#991B1B' 
    },
  ] : [];

  const totalOverdue = chartData.slice(1).reduce((acc, c) => acc + (c.value || 0), 0);

  const columns = [
    {
      title: 'بازه زمانی',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => <span style={{ color: record.color, fontWeight: 'bold' }}>{text}</span>
    },
    {
      title: 'تعداد مطالبات',
      dataIndex: 'count',
      key: 'count',
      render: (val) => val || 0
    },
    {
      title: 'مبلغ (تومان)',
      dataIndex: 'value',
      key: 'value',
      render: (val) => formatPriceToman(val)
    }
  ];

  return (
    <MainLayout>
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto animate-fade-in">
        <div className="mb-6">
          <Title level={3} className="m-0 text-gray-800 dark:text-gray-100 font-bold">
            گزارش تحلیل سنی بدهی‌ها (Aging Report)
          </Title>
          <Text className="text-gray-500 dark:text-gray-400">
            بررسی وضعیت مطالبات بر اساس زمان تاخیر
          </Text>
        </div>

        {error && (
          <Alert
            message="خطا"
            description={error}
            type="error"
            showIcon
            className="mb-6"
          />
        )}

        {data && (
          <>
            <Row gutter={[16, 16]} className="mb-6">
              <Col xs={24} lg={12}>
                <Card className="shadow-sm rounded-2xl dark:bg-gray-800" title="نمودار سنی بدهی‌ها">
                  <div className="h-80" dir="ltr">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis dataKey="name" tick={{ fill: '#888' }} />
                        <YAxis orientation="right" tickFormatter={(val) => `${(val / 1000000).toFixed(0)}M`} tick={{ fill: '#888' }} />
                        <Tooltip 
                          formatter={(val) => [formatPriceToman(val), 'مبلغ (تومان)']}
                          contentStyle={{ borderRadius: '8px', textAlign: 'right' }}
                        />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              </Col>
              
              <Col xs={24} lg={12}>
                <Card className="shadow-sm rounded-2xl dark:bg-gray-800 h-full" title="جدول تفکیک بدهی‌ها">
                  <Table 
                    dataSource={chartData} 
                    columns={columns} 
                    pagination={false} 
                    rowKey="name"
                    size="middle"
                    className="custom-table"
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                          <Text strong>مجموع معوقات (بیش از ۱ روز)</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <Text strong>{chartData.slice(1).reduce((acc, c) => acc + (c.count || 0), 0)} قسط</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>
                          <Text type="danger" strong>{formatPriceToman(totalOverdue)}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default AgingReport;
